import type { ISeries } from '@/src/contexts/SeriesContext'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { getCenterContentAreaHeight } from '@/src/shared/utils'
import { DASHBOARD_CARD_PADDING } from '@/src/utils/constants'
import { setMinutes, startOfMinute, subMinutes } from 'date-fns'
import { t } from 'i18next'
import { TABLE_HEADER_AND_CONTENT_GAP, TABLE_HEADER_HEIGHT, TABS_BOTTOM_MARGIN } from './analyticsConstants'
import { granularityValueMap } from './visualization/hooks/useVisualizationData.hook'

/** Gets the closest past quarter hour and the start date 30 minutes before that */
export const getClosestPastQuarterHourAndStartDate = (date = new Date()) => {
  const endDate = startOfMinute(setMinutes(date, Math.floor(date.getMinutes() / 15) * 15))
  const startDate = subMinutes(endDate, 30)
  return { startDate, endDate }
}

export const getDaysOfGraphData = (graphData: any[], granularity: string) => {
  let days = 0
  if (graphData && granularity === GranularityEnum.ONE_DAY) {
    days = graphData.length
  } else if (graphData && granularity === GranularityEnum.FIFTEEN_MINUTES) {
    days = Math.ceil(graphData.length / 96)
  } else if (graphData && granularity === GranularityEnum.ONE_HOUR) {
    days = Math.ceil(graphData.length / 24)
  } else if (graphData && granularity === GranularityEnum.ONE_MONTH) {
    days = Math.ceil(graphData.length / 365)
  }

  return days
}

export const getTableContainerHeight = (modifier?: string) => {
  const tableContainerHeight = getCenterContentAreaHeight({
    // -40px is for the tabs height. Its difficult to parametrize this value because tabs height depends on many elements.
    modifier: `- 44px - ${TABS_BOTTOM_MARGIN} - ${TABLE_HEADER_HEIGHT} - ${TABLE_HEADER_AND_CONTENT_GAP} - ${DASHBOARD_CARD_PADDING} - ${DASHBOARD_CARD_PADDING} ${modifier}`,
  })
  return tableContainerHeight
}

export const getGranularityLabel = (granularity: GranularityEnum): string => {
  switch (granularity) {
    case GranularityEnum.ONE_MINUTE:
      return t('analytics.granularities.one_minute') as string
    case GranularityEnum.FIFTEEN_MINUTES:
      return t('analytics.granularities.fifteen_minutes') as string
    case GranularityEnum.ONE_HOUR:
      return t('analytics.granularities.one_hour') as string
    case GranularityEnum.ONE_DAY:
      return t('common.day') as string
    case GranularityEnum.ONE_WEEK:
      return t('common.week') as string
    case GranularityEnum.ONE_MONTH:
      return t('common.month') as string
    default:
      return granularity
  }
}

interface ITimeSeriesParamBase {
  name: string
  locationId: number | string
  buildingId: number
}

export const getLabelledTimeSeries = <T extends ITimeSeriesParamBase = any>(locations, timeSeries: T[]): (T & { label: string })[] => {
  const getLocationName = (locationId: string | number): string | null => {
    const location = locations.find(loc => loc.locationPgId === locationId || loc.id === locationId)
    return location ? location.name : null
  }

  const getBuildingName = buildingId => {
    const building = locations[0].buildings.find(bld => bld.id === buildingId)
    return building ? building.name : null
  }

  const updateLabelWithBuilding = (index, locationName) => {
    if (timeSeries[index].name.includes('DWD')) return
    const buildingName = getBuildingName(timeSeries[index].buildingId)
    let label = `${timeSeries[index].name} (${locationName}, ${buildingName})`
    if (!locationName) label = `${timeSeries[index].name} (${t('analytics.building_label', { buildingName })})`
    if (!buildingName) label = `${timeSeries[index].name} (${locationName})`
    // @ts-ignore
    timeSeries[index].label = label
  }

  const nameIndices = {}
  const nameWithLocationIndices = {}

  // Initialize time series with base names and track indices
  for (let i = 0; i < timeSeries.length; i++) {
    const name = timeSeries[i].name
    if (!nameIndices[name]) {
      nameIndices[name] = [i]
      continue
    }

    // Handle the previous occurrence. We need to update the first occurrence of the duplicate because on a first occurrence we don't update the name. Only after a second we know there has been a duplicate.
    if (nameIndices[name]?.length === 1) {
      const firstIndex = nameIndices[name][0]
      const isDwd = timeSeries[firstIndex].name.includes('DWD')
      const locationName = getLocationName(timeSeries[firstIndex].locationId)
      const newLabel = locationName && !isDwd ? `${timeSeries[firstIndex].name} (${locationName})` : timeSeries[firstIndex].name
      // @ts-ignore
      timeSeries[firstIndex].label = newLabel
      if (!nameWithLocationIndices[newLabel]) {
        nameWithLocationIndices[newLabel] = [firstIndex]
      } else {
        updateLabelWithBuilding(firstIndex, locationName)
        nameWithLocationIndices[newLabel].push(firstIndex)
      }
    }

    // Handle the current occurrence
    nameIndices[name].push(i)
    const isDwd = timeSeries[i].name.includes('DWD')
    const locationName = getLocationName(timeSeries[i].locationId)
    const newLabel = locationName && !isDwd ? `${timeSeries[i].name} (${locationName})` : timeSeries[i].name
    // @ts-ignore
    timeSeries[i].label = newLabel

    // Mark occurrence of the label with location. If it is repeated we need to also add the building name.
    if (!nameWithLocationIndices[newLabel]) {
      nameWithLocationIndices[newLabel] = [i]
    } else {
      // If this is the first duplicate, we need to update the first occurrence too.
      if (nameWithLocationIndices[newLabel]?.length === 1) {
        const firstIndex = nameWithLocationIndices[newLabel][0]
        updateLabelWithBuilding(firstIndex, locationName)
      }

      updateLabelWithBuilding(i, locationName)
      nameWithLocationIndices[newLabel].push(i)
    }
  }
  // @ts-ignore
  return timeSeries.map(ts => ({ ...ts, label: ts.label || ts.name }))
}

export const getHighestCommonGranularityOptions = (series: ISeries[], selectedTimeSerieIds: string[], granularityOptions) => {
  const selectedTimeSeries = selectedTimeSerieIds
    .map(serieId => {
      return series.find(serie => serie.id === serieId)
    })
    .filter(Boolean)

  const highestCommonGranularity = getHighestCommonGranularity(selectedTimeSeries)

  const filteredGranularityOptions = granularityOptions.filter(
    option => granularityValueMap[option.value] >= granularityValueMap[highestCommonGranularity]
  )
  return filteredGranularityOptions
}

export const getHighestCommonGranularity = timeSeries => {
  const commonResolution = timeSeries.reduce((acc, timeSerie) => {
    if (timeSerie.resolution.enum > acc) return timeSerie.resolution.enum
    else return acc
  }, GranularityEnum.FIFTEEN_MINUTES)
  return commonResolution
}
